<template>
  <program-plans-getter
    :item-type="itemType"
    :program-plans-setter="programPlansSetter">
    <page-view :id="`${ itemType }s-view`">
      <loader
        v-if="isLoading"
        style="height: 100vh;"/>
      <template v-else>
        <row>
          <sixteen-wide-column>
            <h1>{{ $t(`${ itemType }s.header`) }}</h1>

            <form class="ui form">
              <div
                class="fields"
                style="margin-bottom: 0;">
                <!-- Add New Item Button -->
                <new-item-button :item-type="itemType"/>

                <!-- Add New Plan Button -->
                <new-plan-module
                  :item-type="itemType"
                  :program-plans="programPlans || []"/>
              </div>

              <!-- Search Input -->
              <search-bar
                :placeholder="$t(`${itemType}sSearch.placeholder`)"
                @input="searchCriteria = $event"
                @run-search="search"
                @refresh-items="searchCriteria = ''; search()"/>
            </form>

            <search-summary
              :type="itemType"
              :items="items"
              :query="query"/>
          </sixteen-wide-column>
        </row>

        <!-- Existing Items -->
        <template v-for="(item, index) in items.result">
          <component
            :is="itemComponent"
            :key="`${itemType}-${item.id}`"
            :details-component="detailsComponent"
            :preview-component="previewComponent"
            :affiliated-plans="affiliatedPlansByItem[index] || []"
            :item="item"/>
        </template>

        <row>
          <sixteen-wide-column>
            <pagination
              v-if="items.result.length > 0"
              :items="items"
              @new-page-selected="paginationLink($event)"/>
          </sixteen-wide-column>
        </row>
      </template>
    </page-view>
  </program-plans-getter>
</template>

<script>
import NewItemButton from './NewItemButton'
import NewPlanModule from './NewPlanModule'
import Item from './Item'
import SearchBar from './SearchBar'
import Pagination from './Pagination'
import SearchSummary from './SearchSummary'
import Row from './utility/Row'
import SixteenWideColumn from './utility/SixteenWideColumn'

import { APIError } from '../helpers'

export default {
  name: 'Items',
  components: { SixteenWideColumn, Row, SearchSummary, Pagination, SearchBar, Item, NewPlanModule, NewItemButton },
  props: {
    itemType: {
      type: String,
      required: true
    },
    itemComponent: {
      type: Function,
      required: true
    },
    previewComponent: {
      type: Function,
      required: true
    },
    detailsComponent: {
      type: Function,
      required: true
    }
  },
  data () {
    return {
      isLoading: true,
      query: {
        limit: 5,
        search: '',
        startIndex: 0
      },
      searchCriteria: '',
      items: null,
      programPlans: null
    }
  },
  created () {
    this.query = Object.assign({}, this.query, this.$route.query)

    this.getItems()
  },
  computed: {
    affiliatedPlansByItem () {
      if (!this.items.hasOwnProperty('result') || !this.programPlans) { return [] }

      return (
        this.items.result.map(
          item => {
            return (
              this.programPlans
                .filter(
                  programPlan => {
                    return programPlan.data.childPlans.includes(item.id)
                  }
                )
            )
          }
        )
      )
    }
  },
  methods: {
    search () {
      this.query.search = this.searchCriteria
      this.getItems()
    },
    paginationLink (index) {
      this.query.startIndex = index * this.query.limit
      this.getItems(true)
    },
    getItems (selectedPage) {
      this.isLoading = true
      let query = this.query

      return new Promise(
        (resolve, reject) => {
          // NOTE: Filtering by URL parameter `planType=action` and `search=criteria` don't exist yet
          let requestUrl = `${process.env.VUE_APP_PROGRAM_URL}/programPlans?planType=${this.itemType}`

          // NOTE: This code works only when endpoint returns paginated results filtered by `planType`
          // if (query) {
          //   requestUrl += query.hasOwnProperty('limit') ? `&limit=${query.limit}` : `&limit=100`
          //
          //   for (let key in query) {
          //     if (key !== 'limit' && query[key] !== '') {
          //       requestUrl += `&${key}=${query[key]}`
          //     }
          //   }
          // }

          // NOTE: For Manual Pagination of Result
          requestUrl += `&limit=${1000}`
          requestUrl += `&startIndex=0`

          this.$axios.get(
            requestUrl,
            this.requestConfig
          ).then(
            response => {
              this.searchCriteria = ''

              let { result: paginatedResult } = response.data
              let { result, ...others } = paginatedResult

              // NOTE: This code works only when endpoint returns paginated results filtered by `planType`
              // let items = {
              //   result: result.filter(item => item.planType === this.itemType),
              //   ...others
              // }
              //
              // this.items = items

              // NOTE: For Manual Pagination of Result
              let filteredResult = result.filter(item => item.planType === this.itemType)

              if (this.query.search) {
                filteredResult = this.searchWithinItems(filteredResult)
                if (!selectedPage) { query.startIndex = 0 }
              }

              let limit = query.limit
              let total = filteredResult.length
              let startIndex = query.startIndex

              let manuallyPaginatedItems = {
                result: filteredResult.slice(startIndex, startIndex + limit),
                startIndex,
                total,
                limit
              }

              this.items = manuallyPaginatedItems

              this.isLoading = false
              resolve()
            },
            ({ response }) => {
              reject(new APIError(`Error fetching ${this.itemType}s`, response))
            }
          ).catch(
            error => {
              this.$rollbar.error(this.$rollbar.error(error.message, error.response))
            }
          )
        }
      )
    },
    // NOTE: For Manual Pagination of Result
    searchWithinItems: function (items) {
      let queryTerm = this.query.search.toString().toLowerCase()

      let result = items.filter(
        item => {
          return (
            (Boolean(item.data.shortName) && item.data.shortName.toString().toLowerCase().includes(queryTerm)) ||
            (Boolean(item.data.title) && item.data.title.toString().toLowerCase().includes(queryTerm)) ||
            (Boolean(item.data.body) && item.data.body.toString().toLowerCase().includes(queryTerm))
          )
        }
      )

      return result
    },
    programPlansSetter (itemPlans) {
      this.programPlans = itemPlans
    }
  }
}
</script>

<style scoped>
.row {
  display: flex !important;
  justify-content: center !important;
  width: 100vw;
}
</style>
